import React from 'react';
import {
  TextField, Typography, Box, Grid, Button, Paper, Stack, Chip, Checkbox, FormControlLabel,
} from '@mui/material';

export const VIEWER_COLORS = {
  primary: '#0d6aee',
  primaryDark: '#1d4ed8',
  surfaceBorder: '#dbe7f3',
  surfaceSoft: '#e2e8f0',
  textMain: '#0f172a',
  textMuted: '#475569',
  labelMuted: '#64748b',
};

export const sectionPaperSx = {
  p: { xs: 2, md: 3 },
  borderRadius: 4,
  border: `1px solid ${VIEWER_COLORS.surfaceBorder}`,
  background: '#ffffff',
  boxShadow: '0 12px 28px rgba(15, 23, 42, 0.05)',
};

export const fieldSx = (editable = false) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#ffffff',
    borderRadius: '10px',
    '& fieldset': { borderColor: editable ? '#cbd5e1' : '#d0d7de' },
    '&:hover fieldset': { borderColor: VIEWER_COLORS.primary },
    '&.Mui-focused fieldset': { borderColor: VIEWER_COLORS.primary, borderWidth: 2 },
  },
});

const LabeledField = ({ label, value, onChange, readOnly, editable = false }) => (
  <TextField
    label={label}
    fullWidth
    value={value}
    onChange={onChange}
    InputProps={{ readOnly }}
    size="small"
    sx={fieldSx(editable)}
  />
);

export const CapacidadFuncionalHero = ({ createdAt }) => (
  <Paper
    className="entrevista-viewer__hero"
    elevation={0}
    sx={{
      p: { xs: 2, md: 3 },
      mb: 1,
      borderRadius: 4,
      border: `1px solid ${VIEWER_COLORS.surfaceBorder}`,
      background: 'linear-gradient(135deg, #f8fbff 0%, #edf6ff 55%, #f5fbff 100%)',
      boxShadow: '0 20px 45px rgba(15, 23, 42, 0.08)',
    }}
  >
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      justifyContent="space-between"
      alignItems={{ xs: 'flex-start', sm: 'center' }}
      spacing={1}
    >
      <Box>
        <Typography variant="overline" sx={{ color: VIEWER_COLORS.primaryDark, letterSpacing: 1.4 }}>
          Sistema de seguimiento clínico
        </Typography>
        <Typography variant="h4" sx={{ color: VIEWER_COLORS.textMain, fontWeight: 600, lineHeight: 1.1, mb: 1 }}>
          Capacidad Funcional
        </Typography>
        <Typography sx={{ color: VIEWER_COLORS.textMuted, maxWidth: 760 }}>
          Evaluación SPPB, velocidad de marcha y prueba Timed Up and Go.
        </Typography>
      </Box>
      <Chip
        label={`Registro ${createdAt || '--/--/----'}`}
        sx={{ borderColor: '#bfdbfe', color: '#1e3a8a', fontWeight: 600 }}
        variant="outlined"
      />
    </Stack>
  </Paper>
);

export const CapacidadFuncionalMetrics = ({ metrics }) => (
  <Grid container spacing={2}>
    {metrics.map((metric) => (
      <Grid item xs={12} md={4} key={metric.label}>
        <Paper
          elevation={0}
          sx={{ ...sectionPaperSx, p: 2.2, borderColor: VIEWER_COLORS.surfaceSoft, background: '#fbfdff' }}
        >
          <Typography variant="caption" sx={{ color: VIEWER_COLORS.labelMuted, fontWeight: 700 }}>
            {metric.label.toUpperCase()}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 700, color: VIEWER_COLORS.textMain, lineHeight: 1.2 }}>
            {metric.value}
          </Typography>
          <Typography variant="body2" sx={{ color: VIEWER_COLORS.textMuted }}>
            {metric.unit}
          </Typography>
        </Paper>
      </Grid>
    ))}
  </Grid>
);

export const PatientProfileSection = ({ patientInfo }) => (
  <Paper elevation={0} sx={sectionPaperSx}>
    <Typography variant="h6" sx={{ color: VIEWER_COLORS.textMain, fontWeight: 700, mb: 2 }}>
      Perfil del paciente
    </Typography>
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6} md={3}>
        <LabeledField label="Código" value={patientInfo.codigo} readOnly />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <LabeledField label="Fecha" value={patientInfo.createdAt} readOnly />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <LabeledField label="Nombre" value={patientInfo.nombre} readOnly />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <LabeledField label="Apellidos" value={patientInfo.apellidos} readOnly />
      </Grid>
    </Grid>
  </Paper>
);

export const EquilibrioSection = ({ fields, isEditing, onFieldChange }) => (
  <Paper elevation={0} sx={sectionPaperSx}>
    <Typography variant="h6" sx={{ color: VIEWER_COLORS.textMain, fontWeight: 700, mb: 2 }}>
      Equilibrio estático (SPPB)
    </Typography>
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6} md={3}>
        <LabeledField
          label="Pies juntos (s)"
          value={fields.tiempoPiesJuntos}
          onChange={onFieldChange('tiempoPiesJuntos')}
          readOnly={!isEditing}
          editable={isEditing}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <LabeledField
          label="Tandem (s)"
          value={fields.tiempoTandem}
          onChange={onFieldChange('tiempoTandem')}
          readOnly={!isEditing}
          editable={isEditing}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <LabeledField
          label="Semitandem (s)"
          value={fields.tiempoSemitandem}
          onChange={onFieldChange('tiempoSemitandem')}
          readOnly={!isEditing}
          editable={isEditing}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <LabeledField
          label="Desplazamiento (s)"
          value={fields.tiempoDesplazamiento}
          onChange={onFieldChange('tiempoDesplazamiento')}
          readOnly={!isEditing}
          editable={isEditing}
        />
      </Grid>
    </Grid>
  </Paper>
);

export const MarchaYFuerzaSection = ({ fields, isEditing, onFieldChange }) => (
  <Paper elevation={0} sx={sectionPaperSx}>
    <Typography variant="h6" sx={{ color: VIEWER_COLORS.textMain, fontWeight: 700, mb: 2 }}>
      Marcha y fuerza
    </Typography>
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <LabeledField
          label="Velocidad marcha (m/s)"
          value={fields.velocidadMarcha}
          onChange={onFieldChange('velocidadMarcha')}
          readOnly={!isEditing}
          editable={isEditing}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <LabeledField
          label="Sentadillas (s)"
          value={fields.sentadillas}
          onChange={onFieldChange('sentadillas')}
          readOnly={!isEditing}
          editable={isEditing}
        />
      </Grid>
    </Grid>
  </Paper>
);

export const TUGSection = ({ fields, isEditing, onFieldChange, onCheckChange }) => (
  <Paper elevation={0} sx={sectionPaperSx}>
    <Typography variant="h6" sx={{ color: VIEWER_COLORS.textMain, fontWeight: 700, mb: 1 }}>
      Timed Up and Go (TUG) y puntuación SPPB
    </Typography>
    <Typography variant="caption" sx={{ color: VIEWER_COLORS.textMuted, display: 'block', mb: 2 }}>
      Criterio de fragilidad: TUG ≥ 12 s o puntuación SPPB &lt; 10.
    </Typography>
    <Grid container spacing={2} alignItems="center">
      <Grid item xs={12} md={4}>
        <LabeledField
          label="TUG (s)"
          value={fields.timeUpAndGo}
          onChange={onFieldChange('timeUpAndGo')}
          readOnly={!isEditing}
          editable={isEditing}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <LabeledField
          label="Puntos SPPB"
          value={fields.puntosSPPB}
          onChange={onFieldChange('puntosSPPB')}
          readOnly={!isEditing}
          editable={isEditing}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <FormControlLabel
          control={
            <Checkbox
              checked={fields.falloEjecucion}
              disabled={!isEditing}
              onChange={onCheckChange('falloEjecucion')}
              sx={{ color: VIEWER_COLORS.primary, '&.Mui-checked': { color: VIEWER_COLORS.primary } }}
            />
          }
          label={<Typography sx={{ color: VIEWER_COLORS.textMain }}>Fallo de ejecución</Typography>}
        />
      </Grid>
    </Grid>
  </Paper>
);

export const ViewerActions = ({ isEditing, onEdit, onSave, onCancel, onExportPDF, onDelete }) => (
  <Paper
    className="entrevista-viewer__actions"
    elevation={0}
    sx={{
      mt: 2,
      p: 2,
      borderRadius: 4,
      border: `1px solid ${VIEWER_COLORS.surfaceBorder}`,
      backgroundColor: 'rgba(255,255,255,0.92)',
      backdropFilter: 'blur(10px)',
    }}
  >
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="flex-end" useFlexGap flexWrap="wrap">
      {isEditing ? (
        <>
          <Button variant="outlined" onClick={onCancel} sx={{ borderRadius: 2.5 }}>
            Cancelar edición
          </Button>
          <Button variant="contained" color="primary" onClick={onSave} sx={{ borderRadius: 2.5 }}>
            Guardar cambios
          </Button>
          <Button variant="contained" color="error" onClick={onDelete} sx={{ borderRadius: 2.5 }}>
            Eliminar
          </Button>
        </>
      ) : (
        <>
          <Button variant="outlined" color="primary" onClick={onEdit} sx={{ borderRadius: 2.5 }}>
            Editar
          </Button>
          <Button variant="contained" color="secondary" onClick={onExportPDF} sx={{ borderRadius: 2.5 }}>
            Exportar PDF
          </Button>
          <Button variant="contained" color="error" onClick={onDelete} sx={{ borderRadius: 2.5 }}>
            Eliminar
          </Button>
        </>
      )}
    </Stack>
  </Paper>
);
